'use client';

import { useState } from 'react';
import { ColorIcon, type ColorIconName } from '@/lib/ui-kit/icons/color';
import { cn } from '@/lib/ui-kit/utils';

/**
 * Иконки монет и сетей из бандла (assets/icons-color) — рендер без сетевого
 * запроса. Единая замена COIN_ICON_MAP/iconMap/blockchainIconMap, которые
 * раньше дублировались в каждом диалоге кошелька и ссылались на public/.
 */
const COIN_ICONS: Record<string, ColorIconName> = {
  USDC: 'UsdCoin',
  USDT: 'TetherCoin',
  BTC: 'BtcCoin',
  ETH: 'Eth',
  SOL: 'SolCoin',
  LTC: 'Ltc',
  POL: 'PolygonCoin',
  MATIC: 'PolygonCoin',
  OPL: 'OutpollCoin',
  OUTPOLL: 'OutpollCoin',
  BONUS: 'BonusCoin',
};

const NETWORK_ICONS: Record<string, ColorIconName> = {
  ERC20: 'Eth',
  ETH: 'Eth',
  SOL: 'SolCoin',
  TRC20: 'TrxChain',
  BEP20: 'Bep20Chain',
  POL: 'PolygonCoin',
  MATIC: 'PolygonCoin',
  LTC: 'Ltc',
  'LTC Native': 'Ltc',
  BTC: 'BtcCoin',
};

interface TokenIconProps {
  /** Тикер монеты (USDC, BTC, …) или название сети при variant="network" */
  symbol: string;
  variant?: 'coin' | 'network';
  /** URL с бэкенда — фолбэк для монет, которых нет в локальной карте */
  iconUrl?: string | null;
  size?: number;
  className?: string;
  /** aria-label; без него иконка декоративная (рядом всегда есть тикер текстом) */
  alt?: string;
  /** Что рендерить, когда ни карта, ни iconUrl не дали иконку (initials — первые буквы тикера в сером круге) */
  fallback?: 'outpoll' | 'initials' | 'none';
}

export function TokenIcon({
  symbol,
  variant = 'coin',
  iconUrl,
  size = 20,
  className,
  alt,
  fallback = 'outpoll',
}: TokenIconProps) {
  // Keyed by URL: one broken icon must not hide the next coin's in a reused slot.
  const [brokenUrl, setBrokenUrl] = useState<string | null>(null);
  const a11y = alt ? { role: 'img' as const, 'aria-label': alt } : { 'aria-hidden': true };
  const name = (variant === 'network' ? NETWORK_ICONS : COIN_ICONS)[symbol];
  const isUrlBroken = brokenUrl === iconUrl;
  const handleUrlError = () => setBrokenUrl(iconUrl ?? null);

  if (name) {
    return <ColorIcon name={name} size={size} className={className} {...a11y} />;
  }
  if (iconUrl && !isUrlBroken) {
    return (
      // biome-ignore lint/performance/noImgElement: CDN-иконка неизвестной монеты; next/image требует allowlist хостов и ничего не даёт на SVG
      <img
        src={iconUrl}
        alt={alt ?? ''}
        width={size}
        height={size}
        className={className}
        onError={handleUrlError}
      />
    );
  }
  if (fallback === 'none') return null;
  if (fallback === 'initials') {
    return (
      <span
        {...a11y}
        style={{ width: size, height: size }}
        className={cn(
          'flex shrink-0 items-center justify-center rounded-full bg-v2-btn-secondary text-[10px] font-semibold uppercase text-v2-text-secondary',
          className,
        )}
      >
        {symbol.slice(0, 2)}
      </span>
    );
  }
  return <ColorIcon name="OutpollCoin" size={size} className={className} {...a11y} />;
}
